<script setup lang="ts">
import { $isCodeNode } from "@lexical/code";
import { $isHeadingNode, $isQuoteNode } from "@lexical/rich-text";
import { $isTableCellNode, TableCellHeaderStates } from "@lexical/table";
import { $findMatchingParent, mergeRegister } from "@lexical/utils";
import {
  $getSelection,
  $isElementNode,
  $isParagraphNode,
  $isRangeSelection,
  BLUR_COMMAND,
  COMMAND_PRIORITY_LOW,
  getNearestEditorFromDOMNode,
  type LexicalNode,
} from "lexical";
import { useLexicalComposer } from "lexical-vue";
import { computed, onMounted, onUnmounted, ref } from "vue";

import { usePageStore } from "~/stores";
import { isLexicalStateEmpty } from "~/utils/common";

import { $isToggleDetailsNode } from "../nodes/ToggleDetailsNode";
import { $isToggleTitleNode } from "../nodes/ToggleTitleNode";
import { isEmpty } from "../utils";

const STANDARD_PLACEHOLDER_CLASSES = [
  "relative",
  "before:absolute",
  "before:-z-10",
  "before:left-0",
  "before:top-[var(--placeholder-offset-top,0px)]",
  "before:block",
  "before:max-w-full",
  "before:overflow-hidden",
  "before:whitespace-nowrap",
  "before:text-ellipsis",
  "before-text-faint",
  "before:pointer-events-none",
  "before:content-[attr(data-placeholder)]",
];

const props = defineProps<{
  noAi?: boolean;
}>();

const pageStore = usePageStore();

const aiAvailable = computed(() => pageStore.isOnline && !props.noAi);

const editor = useLexicalComposer();

const hasFocus = ref(false);
const htmlElementRef = ref<HTMLElement | null>(null);

const findTableCell = (node: LexicalNode | null) => (node ? $findMatchingParent(node, $isTableCellNode) : null);

const findParagraph = (node: LexicalNode | null) => (node ? $findMatchingParent(node, $isParagraphNode) : null);

const isEmptyTableCell = (node: LexicalNode | null) => {
  if (!$isTableCellNode(node)) {
    return false;
  }

  if (node.getChildrenSize() !== 1) {
    return node.getChildrenSize() === 0;
  }

  const paragraph = node.getFirstChild();
  return !!paragraph && $isParagraphNode(paragraph) && isEmpty(paragraph);
};

const getPlaceholderTextForNode = (node: LexicalNode) => {
  const tableCellNode = findTableCell(node);
  if ($isTableCellNode(tableCellNode)) {
    return tableCellNode.hasHeaderState(TableCellHeaderStates.ROW) ? "Table header" : "Table data";
  }

  const parent = node.getParent();

  if ($isToggleTitleNode(parent)) {
    return "Title for toggle section";
  }

  if ($isToggleDetailsNode(parent)) {
    return `Content for toggle section${aiAvailable.value ? ", type / for AI" : ""}`;
  }

  if ($isParagraphNode(node)) {
    return aiAvailable.value ? "Type / for AI" : "Write anything";
  }

  if ($isCodeNode(node)) {
    return "Code";
  }

  if ($isQuoteNode(node)) {
    return "Quote";
  }

  if ($isHeadingNode(node)) {
    return "Header";
  }

  return "";
};

const removePlaceholder = () => {
  if (!htmlElementRef.value) {
    return;
  }

  htmlElementRef.value?.removeAttribute("data-placeholder");
  htmlElementRef.value?.style.removeProperty("--placeholder-offset-top");
  htmlElementRef.value?.classList.remove(...STANDARD_PLACEHOLDER_CLASSES);
  htmlElementRef.value = null;
};

const managePlaceholder = () => {
  removePlaceholder();

  const nativeSelection = window.getSelection();
  const selection = $getSelection();

  if (!nativeSelection || !selection || !$isRangeSelection(selection)) {
    return;
  }
  if (getNearestEditorFromDOMNode(nativeSelection.anchorNode) !== editor) {
    return;
  }

  // Prevent from showing when editor is empty
  if (isLexicalStateEmpty(editor.getEditorState().toJSON())) {
    return;
  }

  const node = selection.anchor.getNode();
  const tableCellNode = findTableCell(node);
  if (
    ($isTableCellNode(tableCellNode) && !isEmptyTableCell(tableCellNode)) ||
    (!$isTableCellNode(tableCellNode) && !isEmpty(node)) ||
    ($isElementNode(node) && node.getIndent() > 0)
  ) {
    return;
  }

  // Now switch over to the native selection to get the DOM element
  const htmlDomElement =
    nativeSelection.anchorNode instanceof HTMLElement
      ? nativeSelection.anchorNode
      : (nativeSelection.anchorNode?.parentElement ?? null);
  const placeholderText = getPlaceholderTextForNode(node);

  if (!htmlDomElement || placeholderText === "") {
    return;
  }

  const paragraphNode = findParagraph(node);
  const targetElement =
    tableCellNode && paragraphNode && $isElementNode(paragraphNode)
      ? (editor.getElementByKey(paragraphNode.getKey()) ?? htmlDomElement)
      : htmlDomElement;
  htmlElementRef.value = targetElement;
  htmlElementRef.value.style.setProperty("--placeholder-offset-top", getComputedStyle(targetElement).paddingTop);
  htmlElementRef.value.setAttribute("data-placeholder", placeholderText);
  htmlElementRef.value.classList.add(...STANDARD_PLACEHOLDER_CLASSES);
};

let unregisterListeners: () => void;

onMounted(() => {
  unregisterListeners = mergeRegister(
    editor.registerUpdateListener(({ editorState }) => {
      hasFocus.value = true;
      editorState.read(() => {
        managePlaceholder();
      });
    }),
    editor.registerCommand(
      BLUR_COMMAND,
      () => {
        hasFocus.value = false;
        removePlaceholder();
        return false;
      },
      COMMAND_PRIORITY_LOW
    )
  );
});

onUnmounted(() => {
  removePlaceholder();
  unregisterListeners?.();
});
</script>

<template>
  <slot />
</template>
